import React, { useState, useEffect } from 'react';
import './UserManagement.css';
import { usersAPI } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
import ConfirmDialog from './ConfirmDialog';

function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirm, setShowConfirm] = useState({ show: false, action: null, user: null });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBan = async (user) => {
    if (!hasPermission(currentUser, PERMISSIONS.BAN)) {
      alert('You do not have permission to ban users');
      return;
    }
    setShowConfirm({ show: true, action: 'ban', user });
  };

  const handleLock = async (user) => {
    if (!hasPermission(currentUser, PERMISSIONS.LOCK)) {
      alert('You do not have permission to lock users');
      return;
    }
    try {
      await usersAPI.lockUser(user._id || user.id, !user.locked);
      loadUsers();
    } catch (error) {
      console.error('Failed to lock user:', error);
    }
  };

  const handleDelete = async (user) => {
    if (!hasPermission(currentUser, PERMISSIONS.DELETE)) {
      alert('You do not have permission to delete users');
      return;
    }
    setShowConfirm({ show: true, action: 'delete', user });
  };

  const confirmAction = async () => {
    const { action, user } = showConfirm;
    try {
      if (action === 'ban') {
        // Ban logic would go here
        alert(`User ${user.discordUsername} has been banned`);
      } else if (action === 'delete') {
        // Delete logic would go here
        alert(`User ${user.discordUsername} has been deleted`);
      }
      loadUsers();
    } catch (error) {
      console.error('Failed to perform action:', error);
    } finally {
      setShowConfirm({ show: false, action: null, user: null });
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchQuery ||
      user.discordUsername.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' ||
      (filter === 'locked' && user.locked) ||
      (filter === 'active' && !user.locked);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <LoadingSpinner message="Loading users..." />;
  }

  return (
    <div className="user-management-page">
      <div className="user-management-container">
        <div className="user-management-header">
          <h1>👥 User Management</h1>
          <p>Manage all platform users</p>
        </div>

        <div className="user-management-controls">
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="user-search"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="user-filter"
          >
            <option value="all">All Users</option>
            <option value="active">Active</option>
            <option value="locked">Locked</option>
          </select>
        </div>

        <div className="user-management-stats">
          <div className="stat-mini-card">
            <span className="stat-label">Total Users</span>
            <span className="stat-value">{users.length}</span>
          </div>
          <div className="stat-mini-card">
            <span className="stat-label">Active</span>
            <span className="stat-value">{users.filter(u => !u.locked).length}</span>
          </div>
          <div className="stat-mini-card">
            <span className="stat-label">Locked</span>
            <span className="stat-value">{users.filter(u => u.locked).length}</span>
          </div>
        </div>

        <div className="user-table">
          <div className="user-table-header">
            <div>User</div>
            <div>Email</div>
            <div>Role</div>
            <div>Status</div>
            <div>Actions</div>
          </div>
          {filteredUsers.map(user => (
            <div key={user._id || user.id} className="user-table-row">
              <div className="user-info">
                <span className="user-avatar">👤</span>
                <span className="user-name">{user.discordUsername}</span>
              </div>
              <div className="user-email">{user.email}</div>
              <div className="user-role">
                <span className="role-badge" style={{ background: getRoleColor(user.role) }}>
                  {user.role}
                </span>
              </div>
              <div className="user-status">
                {user.locked ? (
                  <span className="status-badge locked">🔒 Locked</span>
                ) : (
                  <span className="status-badge active">✓ Active</span>
                )}
              </div>
              <div className="user-actions">
                {hasPermission(currentUser, PERMISSIONS.LOCK) && (
                  <button
                    className="action-btn lock-btn"
                    onClick={() => handleLock(user)}
                    title={user.locked ? 'Unlock' : 'Lock'}
                  >
                    {user.locked ? '🔓' : '🔒'}
                  </button>
                )}
                {hasPermission(currentUser, PERMISSIONS.BAN) && (
                  <button
                    className="action-btn ban-btn"
                    onClick={() => handleBan(user)}
                    title="Ban"
                  >
                    🚫
                  </button>
                )}
                {hasPermission(currentUser, PERMISSIONS.DELETE) && (
                  <button
                    className="action-btn delete-btn"
                    onClick={() => handleDelete(user)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                )}
                <button
                  className="action-btn view-btn"
                  onClick={() => setSelectedUser(user)}
                  title="View Details"
                >
                  👁️
                </button>
              </div>
            </div>
          ))}
        </div>

        {selectedUser && (
          <div className="user-details-modal" onClick={() => setSelectedUser(null)}>
            <div className="user-details-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-btn" onClick={() => setSelectedUser(null)}>×</button>
              <h2>User Details</h2>
              <div className="user-details-grid">
                <div><strong>Username:</strong> {selectedUser.discordUsername}</div>
                <div><strong>Email:</strong> {selectedUser.email}</div>
                <div><strong>Role:</strong> {selectedUser.role}</div>
                <div><strong>Status:</strong> {selectedUser.locked ? 'Locked' : 'Active'}</div>
                <div><strong>Created:</strong> {new Date(selectedUser.createdAt).toLocaleDateString()}</div>
                <div><strong>Last Login:</strong> {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleDateString() : 'Never'}</div>
              </div>
            </div>
          </div>
        )}

        <ConfirmDialog
          isOpen={showConfirm.show}
          title={showConfirm.action === 'ban' ? 'Ban User' : 'Delete User'}
          message={`Are you sure you want to ${showConfirm.action} ${showConfirm.user?.discordUsername}?`}
          confirmText={showConfirm.action === 'ban' ? 'Ban' : 'Delete'}
          cancelText="Cancel"
          type={showConfirm.action === 'delete' ? 'danger' : 'warning'}
          onConfirm={confirmAction}
          onCancel={() => setShowConfirm({ show: false, action: null, user: null })}
        />
      </div>
    </div>
  );
}

function getRoleColor(role) {
  const colors = {
    founder: '#ffd700',
    'co-founder': '#ff9800',
    manager: '#f44336',
    administrator: '#f44336',
    'head-admin': '#f44336',
    admin: '#2196f3',
    mod: '#2196f3',
    user: '#aab1c7',
  };
  return colors[role] || '#aab1c7';
}

export default UserManagement;

